/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  MouseEvent,
  MouseEventHandler,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useState
} from 'react';
import MaterialIconButton, {
  IconButtonProps as MUIIconButtonProps
} from '@material-ui/core/IconButton';
import {
  createTheme,
  makeStyles,
  ThemeProvider
} from '@material-ui/core/styles';
import classnames from 'classnames';
import omit from 'lodash.omit';
import {GenericObject} from '@ion/core/src/types/entities';
import {getValidElementId} from '@ion/core/src/utils/StringUtil';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {useExtendStyles} from '../../../hooks/useExtendStyles';
import {
  type IconButtonProps,
  IconButtonLabelPlacement
} from '../IconButton';
import {StableButtonLabel} from './StableButtonLabel';

type ControlStyleProps = {
  controlButtonSize: number;
  controlButtonPadding: number;
  controlFrameSize: number;
  controlDefaultBorderWidth: number;
  controlDefaultFilter?: string;
  controlHoverFilter?: string;
  controlActiveOuterShadow?: string;
  controlActiveFilter?: string;
  controlActiveInnerShadow?: string;
};

const iconButtonThemeBlack = (dlscolor) => {
  const primaryColor = dlscolor;
  return createTheme({
    overrides : {
      MuiSvgIcon : {
        colorPrimary : {
          color : primaryColor
        }
      }
    }
  });
};

const useStyles = (): GenericObject => {
  const background = ({disableHover, disableHoverColor}: IconButtonProps) =>
    !disableHover ? dlsAlt('components', 'btn', 'iconBackground') : (disableHoverColor || 'inherit');
  const isHorizontal = ({labelPlacement}: Pick<IconButtonProps, 'labelPlacement'>) =>
    labelPlacement === IconButtonLabelPlacement.HORIZONTAL;
  return {
    root : {
      padding         : ({padding, label}: IconButtonProps) => label ? '4px 8px' : (padding || null),
      gap             : ({label, labelPlacement}: IconButtonProps) => label ? (isHorizontal({labelPlacement}) ? '8px' : '4px') : null,
      width           : ({label, labelPlacement}: IconButtonProps) => label ? (isHorizontal({labelPlacement}) ? 'auto' : '61px') : null,
      height          : ({label, labelPlacement}: IconButtonProps) => label ? (isHorizontal({labelPlacement}) ? '32px' : '52px') : null,
      minWidth        : ({label, labelPlacement}: IconButtonProps) => label ? (isHorizontal({labelPlacement}) ? 'auto' : '61px') : null,
      minHeight       : ({label, labelPlacement}: IconButtonProps) => label ? (isHorizontal({labelPlacement}) ? '32px' : '52px') : null,
      borderRadius    : ({label}: IconButtonProps) => label ? '8px' : null,
      boxSizing       : ({label}: IconButtonProps) => label ? 'border-box' : null,
      flexDirection   : ({label, labelPlacement}: IconButtonProps) => label ? (isHorizontal({labelPlacement}) ? 'row' : 'column') : null,
      justifyContent  : ({label}: IconButtonProps) => label ? 'center' : null,
      alignItems      : ({label}: IconButtonProps) => label ? 'center' : null,
      color           : ({label}: IconButtonProps) => label ? dlsColor('grey1') : null,
      backgroundColor : ({backgroundColor}: IconButtonProps) => backgroundColor || 'none',
      boxShadow       : ({backgroundColor}: IconButtonProps) => {
        return backgroundColor ? ' 0px 4px 4px rgba(0, 0, 0, 0.25);' : 'none';
      },
      '&:hover' : {
        backgroundColor : ({label}: IconButtonProps) => label ? dlsColor('grey6') : undefined,
        background      : ({label}: IconButtonProps) => label ? undefined : background,
        boxShadow       : ({label}: IconButtonProps) => label ? '0px 1px 2px rgba(0, 0, 0, 0.12)' : undefined,
        color           : ({label}: IconButtonProps) => label ? dlsColor('grey1') : undefined
      },
      '&:active' : {
        backgroundColor : ({label}: IconButtonProps) => label ? dlsColor('aquamarine') : undefined,
        background      : ({label}: IconButtonProps) => label ? undefined : background,
        boxShadow       : ({label}: IconButtonProps) => label ? '0px 1px 2px rgba(0, 0, 0, 0.12)' : undefined,
        color           : ({label}: IconButtonProps) => label ? dlsColor('darkTeal') : undefined
      },
      '&[aria-pressed="true"]' : {
        backgroundColor : ({label}: IconButtonProps) => label ? dlsColor('paleBlue') : undefined,
        color           : ({label}: IconButtonProps) => label ? dlsColor('darkTeal') : undefined
      },
      '&:focus' : {
        outline : 'none'
      },
      '&:focus-visible' : {
        backgroundColor : ({label}: IconButtonProps) => label ? dlsColor('white') : undefined,
        outline         : ({label}: IconButtonProps) => label ? `2px solid ${dlsColor('skyBlue')}` : undefined,
        outlineOffset   : ({label}: IconButtonProps) => label ? '2px' : undefined,
        color           : ({label}: IconButtonProps) => label ? dlsColor('grey1') : undefined
      },
      '&:disabled' : {
        backgroundColor : ({label, isDisableBackgroundColor}: IconButtonProps) =>
          label ? dlsColor('grey8') : undefined,
        background : ({label, isDisableBackgroundColor}: IconButtonProps) =>
          label ? undefined : (isDisableBackgroundColor ? dlsColor('white') : ''),
        color : ({label}: IconButtonProps) => label ? dlsColor('grey4') : undefined
      },
      '& svg' : {
        width  : ({label}: IconButtonProps) => label ? '24px' : undefined,
        height : ({label}: IconButtonProps) => label ? '24px' : undefined
      }
    },
    disabled    : {},
    buttonLabel : {
      display        : ({label}: IconButtonProps) => label ? 'flex' : undefined,
      flexDirection  : ({label, labelPlacement}: IconButtonProps) => label ? (isHorizontal({labelPlacement}) ? 'row' : 'column') : undefined,
      justifyContent : ({label}: IconButtonProps) => label ? 'center' : undefined,
      alignItems     : ({label}: IconButtonProps) => label ? 'center' : undefined,
      gap            : ({label, labelPlacement}: IconButtonProps) => label ? (isHorizontal({labelPlacement}) ? '8px' : '4px') : undefined
    },
    textLabel : {
      display       : ({hideLabel}: IconButtonProps) => hideLabel ? 'none' : undefined,
      color         : 'inherit',
      fontFamily    : 'Inter',
      fontSize      : '12px',
      fontWeight    : 500,
      lineHeight    : '16px',
      letterSpacing : '0px',
      maxWidth      : ({labelPlacement}: IconButtonProps) => isHorizontal({labelPlacement}) ? 'none' : '45px',
      overflow      : 'hidden',
      textAlign     : 'center',
      textOverflow  : ({labelPlacement}: IconButtonProps) => isHorizontal({labelPlacement}) ? 'clip' : 'ellipsis',
      whiteSpace    : 'nowrap'
    },
    rippleVisible : {
      opacity   : 0.01,
      animation : '$mui-ripple-enter 100ms cubic-bezier(0.4, 0, 0.2, 1)'
    },
    '@keyframes mui-ripple-enter' : {
      '0%' : {
        opacity : 0.01
      },
      '100%' : {
        opacity : 0.01
      }
    }
  };
};

const useControlStyles = makeStyles(() => ({
  root : {
    width            : ({controlButtonSize}: ControlStyleProps) => controlButtonSize,
    height           : ({controlButtonSize}: ControlStyleProps) => controlButtonSize,
    minWidth         : ({controlButtonSize}: ControlStyleProps) => controlButtonSize,
    minHeight        : ({controlButtonSize}: ControlStyleProps) => controlButtonSize,
    padding          : ({controlButtonPadding}: ControlStyleProps) => controlButtonPadding,
    borderRadius     : 8,
    boxSizing        : 'border-box',
    display          : 'flex',
    alignItems       : 'center',
    justifyContent   : 'center',
    outline          : 'none',
    backgroundColor  : 'transparent',
    '& $iconWrapper' : {
      border    : ({controlDefaultBorderWidth}: ControlStyleProps) => `${controlDefaultBorderWidth}px solid ${dlsColor('grey5')}`,
      boxShadow : 'none',
      filter    : ({controlDefaultFilter}: ControlStyleProps) => controlDefaultFilter || 'none'
    },
    '&:hover' : {
      backgroundColor : 'transparent',
      filter          : ({controlHoverFilter}: ControlStyleProps) => controlHoverFilter || 'none'
    },
    '&:hover $iconWrapper' : {
      border    : `1px solid ${dlsColor('grey5')}`,
      boxShadow : '0px 1px 2px rgba(0, 0, 0, 0.12)'
    },
    '&:active' : {
      backgroundColor : 'transparent',
      boxShadow       : ({controlActiveOuterShadow}: ControlStyleProps) => controlActiveOuterShadow || 'none',
      filter          : ({controlActiveFilter}: ControlStyleProps) => controlActiveFilter || 'none'
    },
    '&:active $iconWrapper' : {
      border    : `1px solid ${dlsColor('grey5')}`,
      boxShadow : ({controlActiveInnerShadow}: ControlStyleProps) =>
        controlActiveInnerShadow || '0px 1px 1px rgba(0, 0, 0, 0.06), 0px 1px 2px rgba(0, 0, 0, 0.12)',
      filter : 'none'
    },
    '&:focus' : {
      backgroundColor : 'transparent'
    },
    '&:focus-visible' : {
      backgroundColor : 'transparent',
      filter          : 'none'
    },
    '&:focus-visible $iconWrapper' : {
      border    : `2px solid ${dlsColor('skyBlue')}`,
      boxShadow : 'none',
      filter    : 'none'
    },
    '&$disabled $iconWrapper' : {
      border    : `1px solid ${dlsColor('grey5')}`,
      boxShadow : 'none',
      filter    : 'none'
    }
  },
  iconWrapper : {
    width          : ({controlFrameSize}: ControlStyleProps) => controlFrameSize,
    height         : ({controlFrameSize}: ControlStyleProps) => `${controlFrameSize}px !important`,
    borderRadius   : 8,
    boxSizing      : 'border-box',
    background     : dlsColor('white'),
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center'
  },
  disabled : {}
}));

interface WrappedIconButtonProps {
  shouldWrap?: boolean;
  onHoverProps: {
    onMouseEnter?: MouseEventHandler<HTMLElement>;
    onMouseLeave?: MouseEventHandler<HTMLElement>;
  }
}

/**
 * If the given {@link shouldWrap} flag is true, wraps the given children in a wrapping div.
 *
 * This solution was designed after issues with the onMouseLeave were only triggering on window leave, not button leave.
 */
const WrappedIconButton = ({
  children,
  onHoverProps,
  shouldWrap = false
}: PropsWithChildren<WrappedIconButtonProps>) => {
  return shouldWrap ? <div style={{display: 'inherit'}} {...onHoverProps}>{children}</div>
    : React.cloneElement(React.Children.only(children as ReactElement), {
      ref : undefined
    });
};

/**
 *
 * @IconButton
 *
 * Current-theme implementation of `IconButton`.
 *
 * Supports:
 * 1. Default icon-button behavior used across existing screens.
 * 2. `isControlButton` mode for compact framed controls (Back/Forward/Close).
 *
 * ```typescript
 *
 * import React from 'react';
 * import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *       <IconButton icon={<SystemContactIcon size={IconSize.medium} />} />
 *       <br />
 *       <IconButton disabled icon={<SystemContactIcon size={IconSize.medium} />} />
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 */
export const IconButton = forwardRef(({
  elementId,
  icon,
  isControlButton = false,
  controlButtonSize = 24,
  controlButtonPadding = 0,
  controlFrameSize = 24,
  controlIconSize = 24,
  controlDefaultBorderWidth = 1,
  controlDefaultFilter,
  controlHoverFilter,
  controlActiveOuterShadow,
  controlActiveFilter,
  controlActiveInnerShadow,
  disableHoverColor,
  isSelected = false,
  label,
  labelPlacement = IconButtonLabelPlacement.STACKED,
  hideLabel = false,
  labelClass = null,
  disableHover = false,
  backgroundColor = null,
  shouldWrap = false,
  isDisableBackgroundColor = true,
  styleOverrides,
  styleOverridesProps,
  padding = null,
  ...props
}: IconButtonProps, ref) => {
  const controlClasses = useControlStyles({
    controlButtonSize,
    controlButtonPadding,
    controlFrameSize,
    controlDefaultBorderWidth,
    controlDefaultFilter,
    controlHoverFilter,
    controlActiveOuterShadow,
    controlActiveFilter,
    controlActiveInnerShadow
  });
  const classes: GenericObject = useExtendStyles(
    useStyles,
    styleOverrides
  )({
    backgroundColor,
    disableHover,
    isDisableBackgroundColor,
    disableHoverColor,
    padding,
    label,
    labelPlacement,
    hideLabel,
    ...styleOverridesProps
  });
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const {color, disabled} = props;
  const childProps = omit(props, 'color');

  const onMouseEnter = useCallback(function(event: MouseEvent<HTMLElement>): void {
    setIsHovered(true);
    if (childProps.onMouseEnter) {
      childProps.onMouseEnter(event);
    }
  }, [childProps]);

  const onMouseLeave = useCallback(function(event: MouseEvent<HTMLElement>): void {
    setIsHovered(false);
    setIsPressed(false);
    if (childProps.onMouseLeave) {
      childProps.onMouseLeave(event);
    }
  }, [childProps]);

  const onMouseDown = useCallback(function(event: MouseEvent<HTMLElement>): void {
    setIsPressed(true);
    if (childProps.onMouseDown) {
      childProps.onMouseDown(event);
    }
  }, [childProps]);

  const onMouseUp = useCallback(function(event: MouseEvent<HTMLElement>): void {
    setIsPressed(false);
    if (childProps.onMouseUp) {
      childProps.onMouseUp(event);
    }
  }, [childProps]);

  if (isControlButton) {
    return (
      <MaterialIconButton id={getValidElementId(elementId)}
        disableRipple
        ref={ref}
        classes={{
          root     : controlClasses.root,
          disabled : controlClasses.disabled
        }}
        {...(props as any)}>
        <span className={controlClasses.iconWrapper}>
          {React.cloneElement(icon, {
            size  : controlIconSize,
            color : props.disabled ? dlsColor('grey4') : dlsColor('grey1')
          })}
        </span>
      </MaterialIconButton>
    );
  }

  function getIconColor() {
    if (color) {
      return color;
    };
    if (disabled) {
      return dlsColor('grey4');
    }
    if (label) {
      if (isPressed || isSelected) {
        return dlsColor('darkTeal');
      }
      return dlsColor('grey1');
    }
    if (isHovered || isSelected) {
      return dlsColor('aaBlue');
    }
    return dlsColor('grey3');
  }

  return (
    <WrappedIconButton shouldWrap={shouldWrap}
      onHoverProps={{onMouseEnter, onMouseLeave}}>
      <MaterialIconButton disableRipple
        ref={ref}
        onMouseEnter={!shouldWrap ? onMouseEnter : undefined}
        onMouseLeave={!shouldWrap ? onMouseLeave : undefined}
        onMouseDown={!shouldWrap ? onMouseDown : undefined}
        onMouseUp={!shouldWrap ? onMouseUp : undefined}
        id={getValidElementId(elementId)}
        classes={{
          root     : classes.root,
          disabled : classes.disabled,
          label    : classnames(classes.buttonLabel, labelClass)
        }}
        aria-pressed={isSelected ? true : undefined}
        {...childProps}>
        <ThemeProvider theme={iconButtonThemeBlack(getIconColor())}>
          {React.cloneElement(icon, {
            color  : getIconColor(),
            width  : icon.props.size,
            height : icon.props.size
          })}
        </ThemeProvider>
        {label && <StableButtonLabel className={classes.textLabel}>{label}</StableButtonLabel>}
      </MaterialIconButton>
    </WrappedIconButton>
  );
});
